/**
 * EllaLearnAdmin.tsx
 * Manage feeds, run ingestion, view stats and errors.
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Play,
  RefreshCw,
  PlusCircle,
  Database,
  TrendingUp,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Feed {
  id: string;
  jurisdiction: string;
  name: string;
  url: string;
  parser: 'RSS' | 'HTML' | 'PDF_INDEX';
  active: boolean;
  last_fetched: string | null;
  error_count?: number;
  last_error?: string | null;
}

interface FeedStats {
  jurisdiction: string;
  name: string;
  pending: number;
  fetched: number;
  ingested: number;
  failed: number;
  total_items: number;
  last_fetched: string | null;
}

interface ProviderMetric {
  provider: string;
  task: string;
  success_count: number;
  failure_count: number;
  avg_duration_ms: number;
  total_cost: number;
  last_used_at: string | null;
}

export default function EllaLearnAdmin() {
  const { toast } = useToast();
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [stats, setStats] = useState<FeedStats[]>([]);
  const [providerMetrics, setProviderMetrics] = useState<ProviderMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const [jur, setJur] = useState('ZA');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [parser, setParser] = useState<'RSS' | 'HTML' | 'PDF_INDEX'>('RSS');

  async function load() {
    setLoading(true);
    try {
      const [
        { data: f, error: feedsError },
        { data: s, error: statsError },
        { data: m, error: metricsError },
      ] = await Promise.all([
        supabase.from('ella_feeds').select('*').order('jurisdiction, name'),
        supabase.from('ella_learn_stats').select('*'),
        supabase
          .from('ella_provider_metrics')
          .select('*')
          .order('last_used_at', { ascending: false }),
      ]);

      if (feedsError) throw feedsError;
      if (statsError) throw statsError;
      if (metricsError) console.warn('Could not load metrics:', metricsError);

      setFeeds((f || []) as Feed[]);
      setStats(s || []);
      setProviderMetrics((m || []) as ProviderMetric[]);
    } catch (error: any) {
      toast({ title: 'Error loading feeds', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  async function addFeed() {
    if (!name.trim() || !url.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Feed name and URL are required',
        variant: 'destructive',
      });
      return;
    }
    try {
      new URL(url);
    } catch {
      toast({
        title: 'Invalid URL',
        description: 'Please enter a valid URL',
        variant: 'destructive',
      });
      return;
    }
    try {
      const { error } = await supabase.from('ella_feeds').insert({
        jurisdiction: jur,
        name: name.trim(),
        url: url.trim(),
        parser,
        active: true,
      });
      if (error) throw error;
      toast({ title: 'Feed Added', description: `${name} has been added successfully` });
      setName('');
      setUrl('');
      load();
    } catch (error: any) {
      toast({ title: 'Error adding feed', description: error.message, variant: 'destructive' });
    }
  }

  async function reactivateFeed(feedId: string) {
    try {
      const { error } = await supabase
        .from('ella_feeds')
        .update({ active: true, error_count: 0, last_error: null })
        .eq('id', feedId);
      if (error) throw error;
      toast({ title: 'Feed Reactivated', description: 'The feed has been re-enabled.' });
      load();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  }

  async function runIngest() {
    setRunning(true);
    toast({
      title: 'Starting Ingestion',
      description: 'Processing feeds and discovering new content...',
    });

    try {
      const { data, error } = await supabase.functions.invoke('ella-learn-ingest');

      if (error) {
        console.error('Edge function error:', error);
        // Timeout or network error — ingestion may still be running
        toast({
          title: 'Ingestion may still be running',
          description:
            'The request timed out but feeds may still be processing. Refresh to see results.',
          variant: 'default',
        });
        load();
        return;
      }

      if (!data) {
        throw new Error('No data returned from edge function');
      }

      const feedsFailed = data.feeds_failed || 0;
      const failedNames = data.failed_feed_names || [];
      const itemsIngested = data.items_ingested || 0;
      const feedsChecked = data.feeds_checked || 0;

      if (feedsFailed > 0 && itemsIngested > 0) {
        // Partial success
        toast({
          title: 'Ingestion Complete (with warnings)',
          description: `Ingested ${itemsIngested} items from ${feedsChecked - feedsFailed} feeds. ${feedsFailed} feed(s) failed: ${failedNames.join(', ')}`,
        });
      } else if (feedsFailed > 0 && itemsIngested === 0) {
        toast({
          title: 'Ingestion Failed',
          description: `All ${feedsFailed} feed(s) failed: ${failedNames.join(', ')}`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Ingestion Complete',
          description: `Processed ${feedsChecked} feeds, ingested ${itemsIngested} items`,
        });
      }

      load();
    } catch (error: any) {
      console.error('Ingestion error:', error);
      toast({
        title: 'Ingestion may still be running',
        description:
          'The request timed out but feeds may still be processing. Refresh to see results.',
        variant: 'default',
      });
      load();
    } finally {
      setRunning(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            <CardTitle>
              Ella Learn – Multi-LLM Feed Management
              <span className="block text-xs text-muted-foreground font-normal mt-1">
                Powered by Anthropic, Cohere, Mistral, xAI & OpenAI
              </span>
            </CardTitle>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={runIngest} disabled={running}>
              {running ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Now
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add New Feed */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Add New Feed</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <Input
              placeholder="Jurisdiction (e.g., ZA)"
              value={jur}
              onChange={(e) => setJur(e.target.value.toUpperCase())}
              maxLength={5}
            />
            <Input
              placeholder="Feed name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
            />
            <Input
              placeholder="Feed URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="md:col-span-2"
            />
            <div className="flex gap-2">
              <Select value={parser} onValueChange={(v) => setParser(v as any)}>
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="RSS">RSS Feed</SelectItem>
                  <SelectItem value="HTML">HTML Index</SelectItem>
                  <SelectItem value="PDF_INDEX">PDF Index</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={addFeed} size="sm">
                <PlusCircle className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Provider Metrics */}
            {providerMetrics.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">AI Provider Performance</h3>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Provider</TableHead>
                        <TableHead>Task</TableHead>
                        <TableHead className="text-right">Success</TableHead>
                        <TableHead className="text-right">Failed</TableHead>
                        <TableHead className="text-right">Avg Time (ms)</TableHead>
                        <TableHead className="text-right">Total Cost</TableHead>
                        <TableHead>Last Used</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {providerMetrics.map((m, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs capitalize font-medium">
                            {m.provider}
                          </TableCell>
                          <TableCell className="capitalize">{m.task}</TableCell>
                          <TableCell className="text-right text-green-600 dark:text-green-400 font-semibold">
                            {m.success_count}
                          </TableCell>
                          <TableCell className="text-right text-red-600 dark:text-red-400">
                            {m.failure_count}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {Math.round(m.avg_duration_ms)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            ${m.total_cost.toFixed(4)}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {m.last_used_at ? new Date(m.last_used_at).toLocaleString() : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Feed Stats */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Feed Statistics</h3>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Jurisdiction</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                      <TableHead className="text-right">Fetched</TableHead>
                      <TableHead className="text-right">Ingested</TableHead>
                      <TableHead className="text-right">Failed</TableHead>
                      <TableHead>Last Fetched</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">
                          No feed statistics available
                        </TableCell>
                      </TableRow>
                    ) : (
                      stats.map((s, index) => (
                        <TableRow key={`${s.jurisdiction}-${s.name}-${index}`}>
                          <TableCell className="font-medium">{s.jurisdiction}</TableCell>
                          <TableCell>{s.name}</TableCell>
                          <TableCell className="text-right">{s.pending}</TableCell>
                          <TableCell className="text-right">{s.fetched}</TableCell>
                          <TableCell className="text-right text-green-600 dark:text-green-400">
                            {s.ingested}
                          </TableCell>
                          <TableCell className="text-right text-red-600 dark:text-red-400">
                            {s.failed}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {s.last_fetched ? new Date(s.last_fetched).toLocaleString() : '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Feeds List */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Configured Feeds</h3>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Jurisdiction</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>URL</TableHead>
                      <TableHead>Parser</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Health</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feeds.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          No feeds configured. Add your first feed above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      feeds.map((f) => (
                        <TableRow key={f.id}>
                          <TableCell className="font-medium">{f.jurisdiction}</TableCell>
                          <TableCell>{f.name}</TableCell>
                          <TableCell className="max-w-md truncate">
                            <a
                              href={f.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {f.url}
                            </a>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary">
                              {f.parser}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                f.active
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                              }`}
                            >
                              {f.active ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {(f.error_count || 0) > 0 ? (
                              <div className="flex items-center gap-1">
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                                <span
                                  className="text-xs text-amber-600 dark:text-amber-400"
                                  title={f.last_error || ''}
                                >
                                  {f.error_count} error{(f.error_count || 0) > 1 ? 's' : ''}
                                </span>
                                {!f.active && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-1 ml-1"
                                    onClick={() => reactivateFeed(f.id)}
                                    title="Re-enable feed"
                                  >
                                    <RotateCcw className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">OK</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
